/**
 * The messages between the app and the EPUB worker (part 20, table E): the app sends the book as the PDF's table N reads
 * it, with the export's moment; the worker says how far it has come and answers with the file, and how many images it
 * couldn't take, or that there is none. Each side reads what it receives field by field (Definition of Done, security),
 * so a message that isn't one of these is never taken for one.
 */
import { readExportBook } from '@cockatrice/layout';
import type { PdfBook } from '@cockatrice/layout';

/** E1: the EPUB of a book, made at `modified` (milliseconds since the epoch, the package's `dcterms:modified`). */
export interface EpubJob {
  readonly kind: 'epub';
  readonly book: PdfBook;
  readonly modified: number;
}

/** E2–E4: what the worker sends back. */
export type EpubReply =
  | { readonly kind: 'progress'; readonly done: number; readonly total: number }
  | { readonly kind: 'done'; readonly file: Blob; readonly missing: number }
  | { readonly kind: 'failed' };

/** E1: the last moment a `Date` holds (the year 275760), so the stamp is always a date. */
export const MAX_MODIFIED = 8.64e15;

/** The own fields of a plain object with exactly `keys`, or null (part 22's DOCX job reads its fields so too). */
export function fieldsOf(value: unknown, keys: readonly string[]): ReadonlyMap<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const fields = new Map(Object.entries(value));
  return fields.size === keys.length && keys.every((key) => fields.has(key)) ? fields : null;
}

/** Whether `value` is a whole number from 0. */
export function isCount(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) >= 0;
}

/** E1: the job a message asks for, or null for anything else (E5). */
export function readJob(value: unknown): EpubJob | null {
  const fields = fieldsOf(value, ['kind', 'book', 'modified']);
  const modified = fields?.get('modified');
  if (fields?.get('kind') !== 'epub' || !isCount(modified) || modified > MAX_MODIFIED) return null;
  try {
    return { kind: 'epub', book: readExportBook(fields.get('book')), modified };
  } catch {
    return null;
  }
}

/** E2–E4: the reply a message is, or null for anything else (E5). */
export function readReply(value: unknown): EpubReply | null {
  const kind: unknown = typeof value === 'object' && value !== null ? Reflect.get(value, 'kind') : undefined;
  if (kind === 'progress') {
    const fields = fieldsOf(value, ['kind', 'done', 'total']);
    const done = fields?.get('done');
    const total = fields?.get('total');
    return isCount(done) && isCount(total) && done <= total ? { kind, done, total } : null;
  }
  if (kind === 'done') {
    const fields = fieldsOf(value, ['kind', 'file', 'missing']);
    const file = fields?.get('file');
    const missing = fields?.get('missing');
    return file instanceof Blob && isCount(missing) ? { kind, file, missing } : null;
  }
  return kind === 'failed' && fieldsOf(value, ['kind']) !== null ? { kind } : null;
}
